import { ErrorBoundary, injectStores } from "@intrasync/react";
import type { RouterStore } from "@intrasync/stores";
import { Alert, AlertTitle, Box, Button, Paper, Typography } from "@mui/material";
import { isAdmin } from "auth/tier";
import type RootStore from "stores/RootStore";

/**
 * The one way a page says "this data did not load" — never an empty table,
 * never a zero. Two shapes:
 *
 * - `variant="page"` (default): a centred card with a title, the reason and a
 *   Retry button, for a page that has nothing else to show. Same treatment
 *   DevOps Overview already used ("DevOps overview unavailable").
 * - `variant="inline"`: an error Alert with a Retry action, for a panel, a tab
 *   or the body of a table, where the rest of the page still renders.
 *
 * The message is whatever the load reported. Since the API sanitises driver
 * errors it is either deliberate guidance or "Something went wrong on the
 * server. Reference: <id>" — both safe and useful to show.
 *
 * Both shapes also offer a "Check platform readiness" link (walkthrough 5×5
 * §5.1): pages fail one at a time, but the cause is often one misconfigured
 * capability, and System Health's readiness list is the one checklist that
 * names it. The link shows only to admins — the route is admin-only — and
 * navigates through the RouterStore.
 */
export interface LoadErrorStateProps {
  /** Short statement of what is missing, e.g. "Clients unavailable". */
  title: string;
  /** Why — the load's error message. */
  message?: string | null;
  /** Re-run the load. Omit when there is nothing to retry. */
  onRetry?: () => void;
  variant?: "page" | "inline";
}

const FALLBACK = "This data could not be loaded.";

/** The route whose readiness list names a misconfigured capability. */
export const READINESS_ROUTE = "SystemHealth";

interface ReadinessLinkProps {
  rootStore?: RootStore;
  routerStore?: RouterStore;
}

/**
 * The plain (un-injected) link, exported so it can be exercised without a
 * store Provider. Renders nothing for a non-admin or outside the app shell.
 */
export function ReadinessLinkView({ rootStore, routerStore }: ReadinessLinkProps) {
  if (!rootStore || !routerStore || !isAdmin(rootStore)) return null;
  return (
    <Button
      color="inherit"
      size="small"
      onClick={() => void routerStore.goTo(READINESS_ROUTE)}
    >
      Check platform readiness
    </Button>
  );
}

const ReadinessLink = injectStores(ReadinessLinkView, {
  stores: ["rootStore", "routerStore"],
});

export default function LoadErrorState({
  title,
  message,
  onRetry,
  variant = "page",
}: LoadErrorStateProps) {
  if (variant === "inline") {
    return (
      <ErrorBoundary name="LoadErrorState">
        <Alert
          severity="error"
          role="alert"
          action={
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              <ReadinessLink />
              {onRetry && (
                <Button color="inherit" size="small" onClick={onRetry}>
                  Retry
                </Button>
              )}
            </Box>
          }
        >
          <AlertTitle>{title}</AlertTitle>
          {message || FALLBACK}
        </Alert>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary name="LoadErrorState">
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <Paper role="alert" sx={{ p: 3, maxWidth: 560, width: "100%" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            {message || FALLBACK}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            {onRetry && (
              <Button variant="contained" onClick={onRetry}>
                Retry
              </Button>
            )}
            <ReadinessLink />
          </Box>
        </Paper>
      </Box>
    </ErrorBoundary>
  );
}
